/* SharePoint Sync — link ClientOps clients to SharePoint folders and push
 * documents automatically. Phase 1: one-way ClientOps → SharePoint.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, ExternalLink, Link2, Unlink, FolderPlus, AlertTriangle, CheckCircle2, Cloud } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError, useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDateTime, relativeTime } from "@/lib/constants";

export default function SharePointSync() {
    const { user } = useAuth();
    const isAdmin = user?.role === "firm_admin";

    const [status, setStatus] = useState(null);
    const [map, setMap] = useState([]);
    const [log, setLog] = useState([]);
    const [clients, setClients] = useState([]);
    const [busy, setBusy] = useState(false);
    const [siteUrl, setSiteUrl] = useState("");
    const [rootFolder, setRootFolder] = useState("Client Files");

    async function loadAll() {
        try {
            const [s, m, l, cs] = await Promise.all([
                api.get("/integrations/sharepoint/status"),
                api.get("/integrations/sharepoint/map"),
                api.get("/integrations/sharepoint/sync-log?limit=50"),
                api.get("/clients/"),
            ]);
            setStatus(s.data);
            setMap(m.data.data || []);
            setLog(l.data.data || []);
            setClients(cs.data || []);
            if (s.data.site_url) setSiteUrl(s.data.site_url);
            else if (s.data.suggested_site_url) setSiteUrl(s.data.suggested_site_url);
            if (s.data.root_folder) setRootFolder(s.data.root_folder);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }
    useEffect(() => { loadAll(); }, []);

    // If we've just landed back from the Microsoft OAuth callback, the URL will
    // carry ?outlook=connected|error. Surface a toast + strip the query string.
    useEffect(() => {
        const p = new URLSearchParams(window.location.search);
        const outlook = p.get("outlook");
        const err = p.get("error");
        if (outlook === "connected") {
            toast.success("Microsoft 365 reconnected — SharePoint should be reachable now");
        } else if (outlook === "error") {
            toast.error(err ? `Microsoft sign-in failed: ${err}` : "Microsoft sign-in failed");
        }
        if (outlook) {
            const url = new URL(window.location.href);
            url.searchParams.delete("outlook");
            url.searchParams.delete("error");
            window.history.replaceState({}, "", url.toString());
        }
    }, []);

    async function configure() {
        const url = (siteUrl || "").trim();
        if (!url) { toast.error("Type your SharePoint site URL first — e.g. https://yourorg.sharepoint.com/"); return; }
        if (!/^https:\/\/[a-z0-9-]+\.sharepoint\.com/i.test(url)) {
            toast.error("That doesn't look like a SharePoint URL — should start with https://yourorg.sharepoint.com");
            return;
        }
        setBusy(true);
        try {
            await api.post("/integrations/sharepoint/configure", { site_url: url, root_folder: rootFolder.trim() || "Client Files" });
            toast.success("SharePoint site verified");
            await loadAll();
        } catch (e) {
            toast.error(formatApiError(e));
            // If it was the tenant-consent problem, refresh /status so the
            // dedicated red banner appears with the admin-consent button.
            const errorCode = e?.response?.data?.detail?.error;
            if (errorCode === "needs_admin_consent") {
                await loadAll();
            }
        } finally { setBusy(false); }
    }

    async function runAutoMap() {
        setBusy(true);
        try {
            const { data } = await api.post("/integrations/sharepoint/auto-map");
            toast.success(`${data.linked} linked · ${data.unmatched_folders} unmatched folders · ${data.unmatched_clients} clients without a folder`);
            await loadAll();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    }

    async function linkFolder(folderId, clientId) {
        setBusy(true);
        try {
            await api.post("/integrations/sharepoint/map/link", { folder_id: folderId, client_id: clientId });
            toast.success("Linked");
            await loadAll();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }
    async function unlinkFolder(folderId) {
        setBusy(true);
        try {
            await api.post("/integrations/sharepoint/map/unlink", { folder_id: folderId });
            toast.success("Unlinked");
            await loadAll();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }
    async function createFolder(clientId, name) {
        setBusy(true);
        try {
            await api.post("/integrations/sharepoint/map/create-folder", { client_id: clientId, folder_name: name });
            toast.success("Folder created and linked");
            await loadAll();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    async function reconnect() {
        setBusy(true);
        try {
            // Come back to this page (not /app/settings) after Microsoft's callback,
            // and force the consent screen so the user sees the new SharePoint
            // permissions to approve.
            const { data } = await api.get("/integrations/outlook/login", {
                params: { return_path: "/app/sharepoint", force_consent: 1 },
            });
            if (data?.auth_url) {
                window.location.href = data.auth_url;
                return;
            }
            toast.error("Could not start Microsoft 365 sign-in");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    const linked = useMemo(() => map.filter((r) => r.status === "linked"), [map]);
    const unmatchedFolders = useMemo(() => map.filter((r) => r.status === "unmatched_folder"), [map]);
    const unmatchedClients = useMemo(() => map.filter((r) => r.status === "unmatched_client"), [map]);
    const configured = status?.sharepoint_configured;
    const microsoftConnected = status?.microsoft_connected;

    return (
        <div>
            <PageHeader
                eyebrow="Integrations"
                title="SharePoint — Client Files"
                description="Two-way sync target for client documents. ClientOps mirrors uploads to the matching folder in your SharePoint document library."
                testId="sp-header"
            />

            {/* Connection card */}
            <div className="co-card overflow-hidden">
                <div className="px-6 py-5 flex items-start gap-4 border-b border-[#E4E4E7]">
                    <div className="grid h-12 w-12 place-items-center rounded-lg bg-[#036AC4] text-white shrink-0">
                        <Cloud className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-display text-xl font-medium" data-testid="sp-card-title">Microsoft SharePoint</h3>
                            <StatusBadge
                                status={configured ? "open" : "cancelled"}
                                tone={configured ? "green" : "grey"}
                                label={
                                    !microsoftConnected ? "Microsoft 365 not connected"
                                    : configured ? "Configured"
                                    : "Needs configuration"
                                }
                                testId="sp-connected-badge"
                            />
                        </div>
                        {!microsoftConnected && (
                            <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="sp-ms-disconnected">
                                <div className="font-medium">Microsoft 365 isn&apos;t connected right now.</div>
                                <div className="mt-1">
                                    Click <strong>Reconnect Microsoft 365</strong> to run the consent screen — approve <code>Sites.ReadWrite.All</code> + <code>Files.ReadWrite.All</code> when prompted. If Microsoft says &ldquo;Need admin approval&rdquo;, a Global Admin at your firm has to accept the consent once.
                                </div>
                                <Button size="sm" onClick={reconnect} disabled={busy || !isAdmin} className="mt-2 bg-[#036AC4] hover:bg-[#025499] text-white" data-testid="sp-connect-btn">
                                    <RefreshCw className="h-3.5 w-3.5" /> Reconnect Microsoft 365
                                </Button>
                            </div>
                        )}
                        {microsoftConnected && status?.needs_admin_consent && (
                            <div className="mt-2 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-900" data-testid="sp-needs-admin-consent">
                                <div className="font-medium">SharePoint permission not granted at your tenant level.</div>
                                <div className="mt-1">
                                    You&apos;re signed in as <span className="font-mono">{status?.connected_upn}</span>, but Microsoft returned <code>Sites.ReadWrite.All</code> as unauthorised on your tenant. This is an Azure AD app-registration setting — an <strong>Azure Global Admin</strong> at your firm needs to click the button below once. Individual users can&apos;t self-serve this.
                                </div>
                                <ol className="mt-2 ml-4 list-decimal space-y-0.5">
                                    <li>A Global Admin at your firm opens the button below (or the URL) in their own browser.</li>
                                    <li>They sign in with their admin account and click <strong>Accept</strong> on the consent screen.</li>
                                    <li>Come back here and click <strong>Verify &amp; save</strong> again.</li>
                                </ol>
                                {status?.admin_consent_url && (
                                    <a
                                        href={status.admin_consent_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="mt-2 inline-flex items-center gap-1 rounded-md bg-rose-900 hover:bg-rose-950 text-white px-2.5 py-1.5 text-xs font-medium"
                                        data-testid="sp-admin-consent-link"
                                    >
                                        <ExternalLink className="h-3.5 w-3.5" /> Open tenant-wide admin consent screen
                                    </a>
                                )}
                            </div>
                        )}
                        {microsoftConnected && !status?.needs_admin_consent && status?.needs_reconsent && (
                            <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="sp-needs-reconsent">
                                <div className="font-medium">Microsoft 365 needs one extra permission to access SharePoint.</div>
                                <div className="mt-1">Connected as <span className="font-mono">{status?.connected_upn}</span>, but SharePoint scopes weren&apos;t granted yet. Click &ldquo;Reconnect&rdquo; to re-run the M365 consent screen — the same login, just approving <code>Sites.ReadWrite.All</code> + <code>Files.ReadWrite.All</code>.</div>
                                <Button size="sm" onClick={reconnect} disabled={busy || !isAdmin} className="mt-2 bg-[#036AC4] hover:bg-[#025499] text-white" data-testid="sp-reconnect-btn">
                                    <RefreshCw className="h-3.5 w-3.5" /> Reconnect Microsoft 365
                                </Button>
                            </div>
                        )}
                        {status?.site_web_url && (
                            <a href={status.site_web_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-[#5E636E] hover:text-[#0F1115] font-mono" data-testid="sp-site-link">
                                {status.site_name || status.site_url} <ExternalLink className="h-3 w-3" />
                            </a>
                        )}
                    </div>
                    <Button onClick={runAutoMap} disabled={!configured || busy || !isAdmin} className="bg-[#171717] hover:bg-[#2E2E2E] text-white shrink-0" data-testid="sp-auto-map">
                        {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Working…</> : <><RefreshCw className="h-4 w-4" /> Rescan &amp; auto-match</>}
                    </Button>
                </div>

                {/* Configure form */}
                {microsoftConnected && (
                    <div className="px-6 py-4 border-b border-[#E4E4E7] bg-[#FAFAFA] grid gap-3 md:grid-cols-[2fr,1fr,auto] items-end">
                        <div>
                            <div className="co-label">SharePoint site URL</div>
                            <Input value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} placeholder="e.g. https://yourorg.sharepoint.com/" className="font-mono text-sm placeholder:italic placeholder:text-[#A1A1AA]" data-testid="sp-site-url-input" />
                            <p className="mt-1 text-[11px] text-[#5E636E]">
                                {status?.suggested_site_url
                                    ? <>Auto-detected from your Microsoft 365 account. Edit if wrong.</>
                                    : <>Copy this from your browser when viewing the SharePoint site (the part before <code>/Shared Documents</code>).</>
                                }
                            </p>
                        </div>
                        <div>
                            <div className="co-label">Root folder</div>
                            <Input value={rootFolder} onChange={(e) => setRootFolder(e.target.value)} placeholder="Client Files" className="font-mono text-sm placeholder:italic placeholder:text-[#A1A1AA]" data-testid="sp-root-folder-input" />
                        </div>
                        <Button onClick={configure} disabled={!isAdmin || busy} className="bg-[#036AC4] hover:bg-[#025499] text-white" data-testid="sp-save-config">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Verify &amp; save
                        </Button>
                    </div>
                )}

                <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-[#E4E4E7]">
                    <Stat label="Linked" value={linked.length} sub="client ↔ folder" testId="sp-stat-linked" />
                    <Stat label="Folders" value={unmatchedFolders.length} sub="need a client" testId="sp-stat-um-folders" />
                    <Stat label="Clients" value={unmatchedClients.length} sub="need a folder" testId="sp-stat-um-clients" />
                    <Stat label="Last scan" value={status?.last_sync_at ? relativeTime(status.last_sync_at) : "—"} sub="auto-match" testId="sp-stat-last-scan" />
                </div>

                {status?.last_error && (
                    <div className="px-6 py-3 bg-rose-50 border-t border-rose-200 text-xs text-rose-800 flex items-start gap-2" data-testid="sp-last-error">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5" /> {status.last_error}
                    </div>
                )}
            </div>

            {/* Linked list */}
            <Section title={`Linked (${linked.length})`} testId="sp-section-linked">
                {linked.length === 0 ? <Empty text="No clients linked yet. Run auto-match once your site is configured." /> : (
                    <Table>
                        <THead cols={["Client", "SharePoint folder", "Linked via", ""]} />
                        <tbody className="divide-y divide-[#E4E4E7]">
                            {linked.map((r) => (
                                <tr key={r.folder_id} data-testid={`sp-linked-${r.folder_id}`}>
                                    <td className="px-6 py-2 text-sm">{r.client_name || <span className="text-[#5E636E]">—</span>}</td>
                                    <td className="px-6 py-2 text-sm">
                                        <a href={r.folder_web_url} target="_blank" rel="noreferrer" className="hover:underline inline-flex items-center gap-1">
                                            {r.folder_name} <ExternalLink className="h-3 w-3" />
                                        </a>
                                    </td>
                                    <td className="px-6 py-2 text-xs font-mono text-[#5E636E]">{r.linked_via || "—"}</td>
                                    <td className="px-6 py-2 text-right">
                                        <Button size="sm" variant="outline" onClick={() => unlinkFolder(r.folder_id)} disabled={!isAdmin || busy} data-testid={`sp-unlink-${r.folder_id}`}>
                                            <Unlink className="h-3.5 w-3.5" /> Unlink
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Section>

            {/* Unmatched folders (SP folder → pick a ClientOps client) */}
            {unmatchedFolders.length > 0 && (
                <Section title={`SharePoint folders that need a client (${unmatchedFolders.length})`} testId="sp-section-um-folders">
                    <Table>
                        <THead cols={["SharePoint folder", "Match to client", ""]} />
                        <tbody className="divide-y divide-[#E4E4E7]">
                            {unmatchedFolders.map((r) => (
                                <FolderPickRow key={r.folder_id} row={r} clients={clients} onLink={linkFolder} busy={busy} isAdmin={isAdmin} />
                            ))}
                        </tbody>
                    </Table>
                </Section>
            )}

            {/* Unmatched clients (client → create a new folder in SP) */}
            {unmatchedClients.length > 0 && (
                <Section title={`Clients without a SharePoint folder (${unmatchedClients.length})`} testId="sp-section-um-clients">
                    <Table>
                        <THead cols={["Client", "Suggested folder name", ""]} />
                        <tbody className="divide-y divide-[#E4E4E7]">
                            {unmatchedClients.map((r) => (
                                <ClientCreateRow key={r.client_id_pending} row={r} onCreate={createFolder} busy={busy} isAdmin={isAdmin} />
                            ))}
                        </tbody>
                    </Table>
                </Section>
            )}

            {/* Recent sync log */}
            <Section title="Recent sync activity" testId="sp-section-log">
                {log.length === 0 ? <Empty text="No sync activity yet. Uploads will appear here as they push to SharePoint." /> : (
                    <Table>
                        <THead cols={["When", "File", "Status", "Detail"]} />
                        <tbody className="divide-y divide-[#E4E4E7]">
                            {log.map((r, i) => (
                                <tr key={i} data-testid={`sp-log-${i}`}>
                                    <td className="px-6 py-2 text-xs text-[#5E636E]">{formatDateTime(r.at)}</td>
                                    <td className="px-6 py-2 text-sm">{r.file_name || <span className="text-[#5E636E]">—</span>}</td>
                                    <td className="px-6 py-2">
                                        {r.status === "pushed" ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Pushed</span>
                                        : r.status === "error" ? <span className="inline-flex items-center gap-1 text-xs text-rose-700"><AlertTriangle className="h-3.5 w-3.5" /> Error</span>
                                        : <span className="text-xs text-[#5E636E]">{r.status}</span>}
                                    </td>
                                    <td className="px-6 py-2 text-xs">
                                        {r.web_url ? <a href={r.web_url} target="_blank" rel="noreferrer" className="text-[#036AC4] hover:underline inline-flex items-center gap-1">Open <ExternalLink className="h-3 w-3" /></a>
                                        : r.error ? <span className="text-rose-700">{r.error}</span>
                                        : <span className="text-[#5E636E]">—</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Section>
        </div>
    );
}

function FolderPickRow({ row, clients, onLink, busy, isAdmin }) {
    const [pick, setPick] = useState("");
    return (
        <tr data-testid={`sp-um-folder-${row.folder_id}`}>
            <td className="px-6 py-2 text-sm">
                <a href={row.folder_web_url} target="_blank" rel="noreferrer" className="hover:underline inline-flex items-center gap-1">
                    {row.folder_name} <ExternalLink className="h-3 w-3" />
                </a>
            </td>
            <td className="px-6 py-2">
                <Select value={pick} onValueChange={setPick}>
                    <SelectTrigger className="max-w-md h-8 text-sm" data-testid={`sp-pick-client-${row.folder_id}`}>
                        <SelectValue placeholder="Pick a client…" />
                    </SelectTrigger>
                    <SelectContent>
                        {clients.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </td>
            <td className="px-6 py-2 text-right">
                <Button size="sm" onClick={() => onLink(row.folder_id, pick)} disabled={!pick || !isAdmin || busy} className="bg-[#036AC4] hover:bg-[#025499] text-white" data-testid={`sp-link-btn-${row.folder_id}`}>
                    <Link2 className="h-3.5 w-3.5" /> Link
                </Button>
            </td>
        </tr>
    );
}

function ClientCreateRow({ row, onCreate, busy, isAdmin }) {
    const [name, setName] = useState(row.client_name || "");
    return (
        <tr data-testid={`sp-um-client-${row.client_id_pending}`}>
            <td className="px-6 py-2 text-sm">{row.client_name}</td>
            <td className="px-6 py-2">
                <Input value={name} onChange={(e) => setName(e.target.value)} className="max-w-md h-8 text-sm" data-testid={`sp-folder-name-${row.client_id_pending}`} />
            </td>
            <td className="px-6 py-2 text-right">
                <Button size="sm" onClick={() => onCreate(row.client_id_pending, name)} disabled={!name.trim() || !isAdmin || busy} className="bg-[#036AC4] hover:bg-[#025499] text-white" data-testid={`sp-create-btn-${row.client_id_pending}`}>
                    <FolderPlus className="h-3.5 w-3.5" /> Create &amp; link
                </Button>
            </td>
        </tr>
    );
}

function Stat({ label, value, sub, testId }) {
    return (
        <div className="px-6 py-4" data-testid={testId}>
            <div className="co-label">{label}</div>
            <div className="mt-1 text-2xl font-display">{value}</div>
            <div className="text-xs text-[#5E636E]">{sub}</div>
        </div>
    );
}

function Section({ title, children, testId }) {
    return (
        <div className="mt-6 co-card overflow-hidden" data-testid={testId}>
            <div className="border-b border-[#E4E4E7] px-6 py-3 co-label">{title}</div>
            {children}
        </div>
    );
}

function Table({ children }) {
    return <table className="w-full text-sm">{children}</table>;
}

function THead({ cols }) {
    return (
        <thead className="bg-[#FAFAFA] text-[11px] uppercase tracking-wider text-[#5E636E]">
            <tr>{cols.map((c, i) => <th key={i} className="text-left px-6 py-2">{c}</th>)}</tr>
        </thead>
    );
}

function Empty({ text }) {
    return <div className="p-6 text-sm text-[#5E636E]">{text}</div>;
}
